"use client";

/**
 * P6-18 · The popup dialog: brand-configured headline/body/image + a
 * consent-gated email capture. On success it reveals the single-use reward code
 * (if the popup grants one). Reuses the a11y `Dialog` (focus trap, Esc, portal).
 */
import * as React from "react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { fallBackToOriginal, thumbnailImage } from "~/lib/image-loader";
import type { PopupContent } from "~/lib/marketing/popup-config";
import { api } from "~/trpc/react";

export interface ActivePopup {
  id: string;
  content: PopupContent;
  hasReward: boolean;
}

export function PopupModal({
  popup,
  onClose,
}: {
  popup: ActivePopup;
  onClose: (converted: boolean) => void;
}) {
  const [email, setEmail] = React.useState("");
  const [consent, setConsent] = React.useState(false);
  const [rewardCode, setRewardCode] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const capture = api.marketing.capturePopup.useMutation({
    onSuccess: (res) => setRewardCode(res.rewardCode ?? ""),
    onError: (e) => setError(e.message || "Something went wrong."),
  });
  const captured = capture.isSuccess;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!consent) {
      setError("Please tick the box to receive emails.");
      return;
    }
    capture.mutate({ popupId: popup.id, email: email.trim(), consent });
  };

  const c = popup.content;
  return (
    <Dialog
      open
      onOpenChange={() => onClose(captured)}
      ariaLabel={c.headline}
      placement="center"
    >
      <div className="max-w-md p-6">
        {c.imageUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            {...thumbnailImage(c.imageUrl, 400)}
            onError={fallBackToOriginal(c.imageUrl)}
            alt=""
            className="mb-4 max-h-40 w-full rounded-md object-cover"
          />
        ) : null}
        <DialogHeader>
          <DialogTitle>{captured ? c.successText : c.headline}</DialogTitle>
          {!captured && <DialogDescription>{c.body}</DialogDescription>}
        </DialogHeader>

        {captured ? (
          <div className="mt-4">
            {rewardCode ? (
              <p className="rounded-lg border border-dashed border-border bg-surface-2 p-4 text-center font-mono text-lg font-bold tracking-wide text-fg">
                {rewardCode}
              </p>
            ) : (
              <p className="text-sm text-fg-muted">Thanks for subscribing!</p>
            )}
            <Button className="mt-4 w-full" onClick={() => onClose(true)}>
              Continue shopping
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-3">
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              aria-label="Email address"
            />
            <label className="flex items-start gap-2 text-xs text-fg-muted">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5"
              />
              <span>{c.consentText}</span>
            </label>
            {error ? <p className="text-xs text-danger">{error}</p> : null}
            <Button
              type="submit"
              className="w-full"
              disabled={capture.isPending}
            >
              {capture.isPending ? "…" : c.ctaLabel}
            </Button>
          </form>
        )}
      </div>
    </Dialog>
  );
}
